<script setup lang="ts">
import { Head, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        title: string;
        description: string;
        path?: string;
        image?: string;
        type?: 'website' | 'article';
        keywords?: string[];
        noindex?: boolean;
        jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    }>(),
    {
        path: '/',
        type: 'website',
        noindex: false,
    },
);

const page = usePage<{
    name?: string;
    seo?: {
        appUrl: string;
        name: string;
        description: string;
        ogImage: string;
        locale: string;
        twitter: string;
        facebookAppId?: string | null;
        keywords: string[];
    };
}>();

const seo = computed(() => page.props.seo);
const siteName = computed(() => seo.value?.name ?? page.props.name ?? 'CreativeTint');
const appUrl = computed(() => (seo.value?.appUrl ?? '').replace(/\/$/, ''));
const canonical = computed(() => {
    const path = props.path.startsWith('/') ? props.path : `/${props.path}`;
    return `${appUrl.value}${path === '/' ? '' : path}` || path;
});
const imageUrl = computed(() => {
    const img = props.image ?? seo.value?.ogImage ?? '/images/og-default.png';
    if (img.startsWith('http')) {
        return img;
    }
    return `${appUrl.value}${img.startsWith('/') ? img : `/${img}`}`;
});
const fullTitle = computed(() => props.title);
const keywordList = computed(() => (props.keywords?.length ? props.keywords : seo.value?.keywords ?? []).join(', '));
const robots = computed(() => (props.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'));
const jsonLdPayload = computed(() => {
    if (!props.jsonLd) {
        return null;
    }
    return JSON.stringify(props.jsonLd);
});
</script>

<template>
    <Head :title="fullTitle">
        <meta head-key="description" name="description" :content="description" />
        <meta v-if="keywordList" head-key="keywords" name="keywords" :content="keywordList" />
        <meta head-key="robots" name="robots" :content="robots" />
        <meta head-key="googlebot" name="googlebot" :content="robots" />
        <meta head-key="author" name="author" :content="siteName" />
        <meta head-key="theme-color" name="theme-color" content="#0A0A0F" />
        <link head-key="canonical" rel="canonical" :href="canonical" />

        <!-- Open Graph -->
        <meta head-key="og:locale" property="og:locale" :content="seo?.locale ?? 'en_US'" />
        <meta head-key="og:type" property="og:type" :content="type" />
        <meta head-key="og:site_name" property="og:site_name" :content="siteName" />
        <meta head-key="og:title" property="og:title" :content="fullTitle" />
        <meta head-key="og:description" property="og:description" :content="description" />
        <meta head-key="og:url" property="og:url" :content="canonical" />
        <meta head-key="og:image" property="og:image" :content="imageUrl" />
        <meta head-key="og:image:secure_url" property="og:image:secure_url" :content="imageUrl" />
        <meta head-key="og:image:width" property="og:image:width" content="1200" />
        <meta head-key="og:image:height" property="og:image:height" content="630" />
        <meta head-key="og:image:alt" property="og:image:alt" :content="fullTitle" />
        <meta
            v-if="seo?.facebookAppId"
            head-key="fb:app_id"
            property="fb:app_id"
            :content="seo.facebookAppId"
        />

        <!-- X -->
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" :content="fullTitle" />
        <meta head-key="twitter:description" name="twitter:description" :content="description" />
        <meta head-key="twitter:image" name="twitter:image" :content="imageUrl" />
        <meta v-if="seo?.twitter" head-key="twitter:site" name="twitter:site" :content="seo.twitter" />
        <meta v-if="seo?.twitter" head-key="twitter:creator" name="twitter:creator" :content="seo.twitter" />

        <component :is="'script'" v-if="jsonLdPayload" type="application/ld+json" v-text="jsonLdPayload" />
    </Head>
</template>
